import React from 'react';
import { Link } from 'react-router-dom';
import { Contract, Person, LegalEntity } from '../types/Contract';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import './Dashboard.css';

interface DashboardProps {
  contracts: Contract[];
}

export const Dashboard: React.FC<DashboardProps> = ({ contracts }) => {
  const totalContracts = contracts.length;
  const mainContracts = contracts.filter(c => c.type === 'main').length;
  const preliminaryContracts = contracts.filter(c => c.type === 'preliminary').length;
  const totalAmount = contracts.reduce((sum, c) => sum + Number(c.amount), 0);

  const recentContracts = contracts
    .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
    .slice(0, 5);

  const formatCurrency = (amount: number | string) => {
    const numAmount = Number(amount);
    if (numAmount === 0) return '0 ₽';

    // Форматируем число с разделителями тысяч
    const formatted = numAmount.toLocaleString('ru-RU');
    return `${formatted} ₽`;
  };

  const formatLargeAmount = (amount: number | string) => {
    const numAmount = Number(amount);
    if (numAmount === 0) return '0 ₽';

    if (numAmount >= 1000000) {
      const millions = (numAmount / 1000000).toFixed(1);
      return `${millions} млн ₽`;
    } else if (numAmount >= 1000) {
      const thousands = (numAmount / 1000).toFixed(0);
      return `${thousands} тыс. ₽`;
    } else {
      return `${numAmount.toLocaleString('ru-RU')} ₽`;
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd.MM.yyyy', { locale: ru });
  };

  const getDisplayName = (entity: Person | LegalEntity) => {
    if ((entity as any).type === 'legal_entity') {
      return (entity as LegalEntity).name;
    }
    const person = entity as Person;
    return `${person.lastName} ${person.firstName} ${person.middleName || ''}`.trim();
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Добро пожаловать в систему управления договорами</h1>
        <p>Создавайте, управляйте и генерируйте договоры купли-продажи земельных участков</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📄</div>
          <div className="stat-content">
            <h3>Всего договоров</h3>
            <div className="stat-number">{totalContracts}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>Основных договоров</h3>
            <div className="stat-number">{mainContracts}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <h3>Предварительных договоров</h3>
            <div className="stat-number">{preliminaryContracts}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>Общая сумма сделок</h3>
            <div className="stat-number">{formatLargeAmount(totalAmount)}</div>
          </div>
        </div>
      </div>

      <div className="quick-actions">
        <h2>Быстрые действия</h2>
        <div className="action-buttons">
          <Link to="/create" className="action-button primary">
            <div className="action-icon">➕</div>
            <div className="action-content">
              <h3>Создать договор</h3>
              <p>Создать новый договор купли-продажи</p>
            </div>
          </Link>

          <Link to="/history" className="action-button secondary">
            <div className="action-icon">📚</div>
            <div className="action-content">
              <h3>Просмотреть историю</h3>
              <p>Посмотреть все созданные договоры</p>
            </div>
          </Link>
        </div>
      </div>

      {recentContracts.length > 0 && (
        <div className="recent-contracts">
          <h2>Последние договоры</h2>
          <div className="recent-list">
            {recentContracts.map((contract) => (
              <div key={contract.id} className="recent-item">
                <div className="recent-info">
                  <div className="recent-title">
                    {contract.type === 'main' ? '📄' : '⏳'}
                    {getDisplayName(contract.seller)} → {getDisplayName(contract.buyer)}
                  </div>
                  <div className="recent-details">
                    <span className="recent-amount">{formatCurrency(contract.amount)}</span>
                    <span className="recent-date">{formatDate(contract.createdDate)}</span>
                  </div>
                </div>
                <div className="recent-cadastral">
                  {contract.landPlot.cadastralNumber}
                </div>
              </div>
            ))}
          </div>

          {contracts.length > 5 && (
            <div className="recent-footer">
              <Link to="/history" className="view-all-link">
                Посмотреть все договоры →
              </Link>
            </div>
          )}
        </div>
      )}

      {totalContracts === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>Пока нет договоров</h3>
          <p>Создайте свой первый договор, чтобы начать работу с системой</p>
          <Link to="/create" className="empty-action-btn">
            Создать первый договор
          </Link>
        </div>
      )}
    </div>
  );
}; 